// The day header's net tip total for the Recent Activity list
// ("October 16, 2026 (€56.96 in 2 tips)").
//
// A day's rows are a mix of tips credited in, member payouts (transfers) and
// refunded taps. The header totals ONLY the tips credited that day, using the
// tap's netAmount — what the team (or the member, on a member's page) actually
// receives once Strike's fee is taken — the same field the Summary block above
// the list sums, so the days add up to the summary. Payouts and refunds are
// never in the total; when a day lists any, the header says so with the
// item count ("€56.96 in 2 taps · 3 items") so the count stays honest.
//
// Amounts are in cents (MoneyTitle divides by 100), like every tap field.

const EXCLUDED_STATUSES = ['refunded', 'failed', 'cancelled'];

/** A tap that was credited in: a tip (or payment) the recipient keeps. */
export function isCreditedTip(activity) {
  if (!activity || activity.type !== 'tap') return false;
  if (activity.direction !== 'in') return false;
  if (EXCLUDED_STATUSES.includes(activity.status)) return false;
  return true;
}

/**
 * The tap's net amount in cents. Taps carry netAmount from the backend
 * (amount + customer-paid fee − Strike's fee); older taps without it get the
 * same formula from the parts they do carry.
 */
export function netTipAmount(activity) {
  const { netAmount, amount, fee, strikeFee } = activity || {};
  if (Number.isFinite(Number(netAmount)) && netAmount !== null && netAmount !== undefined) {
    return Number(netAmount);
  }
  return (Number(amount) || 0) + (Number(fee) || 0) - (Number(strikeFee) || 0);
}

/**
 * Totals for one day's rows.
 * @returns {{ tips: Array<{currency: string, amount: number, count: number, kinds: string[]}>, tipCount: number, itemCount: number }}
 *   `tips` is one entry per currency, in first-seen order (a team has one
 *   currency; the shape just never silently adds euro to sterling). `kinds`
 *   is the distinct kind of the counted taps ('Tip', 'Donation', 'Pay'), so
 *   the header can say "2 tips" for a tipping team, "2 donations" for a
 *   giving one and "2 payments" for a paying one; a day that mixes them says
 *   "2 taps".
 */
export function dayActivityTotals(activities) {
  const list = Array.isArray(activities) ? activities : [];
  const byCurrency = {};
  const order = [];

  list.forEach((activity) => {
    if (!isCreditedTip(activity)) return;
    // Upper-cased so 'eur' and 'EUR' (Stripe's native form vs ours) never
    // split one day's euro into two segments.
    const currency = String(activity.currency || 'EUR').toUpperCase();
    if (!byCurrency[currency]) {
      byCurrency[currency] = { currency, amount: 0, count: 0, kinds: [] };
      order.push(currency);
    }
    const total = byCurrency[currency];
    total.amount += netTipAmount(activity);
    total.count += 1;
    const kind = tapKind(activity);
    if (!total.kinds.includes(kind)) total.kinds.push(kind);
  });

  const tips = order.map((currency) => byCurrency[currency]);
  const tipCount = tips.reduce((n, tip) => n + tip.count, 0);
  return { tips, tipCount, itemCount: list.length };
}

// The tap's product is the truth; the list model's activityType only knows
// Tip vs Pay (it labels a donation 'Pay'), so it is the fallback, never first.
const KIND_BY_PRODUCT = {
  tipping: 'Tip',
  giving: 'Donation',
  payment: 'Pay',
};

/** 'Tip' | 'Donation' | 'Pay' for a tap, 'Tap' when it says neither. */
export function tapKind(activity) {
  const { product, activityType } = activity || {};
  return KIND_BY_PRODUCT[product] || activityType || 'Tap';
}

const COUNT_WORDS = {
  Tip: ['tip', 'tips'],
  Donation: ['donation', 'donations'],
  Pay: ['payment', 'payments'],
};
const TAP_WORDS = ['tap', 'taps'];

/**
 * "2 tips" / "1 donation" / "3 payments" / "4 taps": the taps' own word when every counted
 * tap on the day is the same kind, the neutral "taps" when the day mixes them.
 */
export const tapsWord = (count, kinds = []) => {
  const words = (kinds.length === 1 && COUNT_WORDS[kinds[0]]) || TAP_WORDS;
  return count === 1 ? `1 ${words[0]}` : `${count} ${words[1]}`;
};
export const itemsWord = (count) => (count === 1 ? '1 item' : `${count} items`);
